import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

// PKCE Helper Functions
const base64URLEncode = (buffer: Uint8Array): string => {
  return btoa(String.fromCharCode(...buffer))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64URLEncode(array);
};

const generateCodeChallenge = async (verifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest('SHA-256', data);
  return base64URLEncode(new Uint8Array(hash));
};

export const AuthStartMicrosoft = () => {
  const [error, setError] = useState<string | null>(null);
  const [showFallback, setShowFallback] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const initPKCEFlow = async () => {
    try {
      setError(null);
      setIsRetrying(true);
      
      const clientId = '5eaa225e-61a5-407a-93b0-20d0eb819191';
      
      // Check if we have a session ID for polling fallback (Edge/OWA)
      const urlParams = new URLSearchParams(window.location.search);
      const dialogSessionId = urlParams.get('sessionId');
      const isDialogMode = urlParams.get('mode') === 'dialog';
      
      console.log('[AuthStartMicrosoft] Flow detection:', { isDialogMode, hasSessionId: !!dialogSessionId });
      
      // Generate PKCE parameters
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      
      console.log('[AuthStartMicrosoft] PKCE flow started');
      console.log('[AuthStartMicrosoft] Code verifier:', codeVerifier.substring(0, 10) + '...');
      
      // Store verifier in BOTH storages for maximum compatibility
      // WebView2 and Safari ITP may lose sessionStorage on redirect
      const storageKey = 'pkce_verifier_direct_login';
      sessionStorage.setItem(storageKey, codeVerifier);
      localStorage.setItem(storageKey, codeVerifier);
      console.log('[AuthStartMicrosoft] Stored verifier in sessionStorage AND localStorage');
      
      const redirectUri = encodeURIComponent(`${window.location.origin}/auth-callback-microsoft`);
      const scopes = encodeURIComponent('openid profile User.Read Contacts.ReadWrite MailboxSettings.ReadWrite');
      
      // Use session ID as state parameter for polling fallback (Edge/OWA)
      // This allows the callback to store the session in DB for polling
      const stateParam = dialogSessionId ? encodeURIComponent(dialogSessionId) : '';
      
      // PKCE Authorization Code Flow
      const authUrl = `https://login.microsoftonline.com/common/oauth2/v2.0/authorize?` +
        `client_id=${clientId}` +
        `&response_type=code` +
        `&redirect_uri=${redirectUri}` +
        `&scope=${scopes}` +
        `&response_mode=query` +
        `&code_challenge=${codeChallenge}` +
        `&code_challenge_method=S256` +
        (stateParam ? `&state=${stateParam}` : '');
      
      console.log('[AuthStartMicrosoft] Redirecting to Microsoft OAuth with PKCE...');
      if (dialogSessionId) {
        console.log('[AuthStartMicrosoft] Polling fallback enabled with session:', dialogSessionId);
      }
      window.location.href = authUrl;
    } catch (err) {
      console.error('[AuthStartMicrosoft] PKCE flow error:', err);
      setError('Failed to initialize authentication');
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    initPKCEFlow();
    
    // Show fallback button after 4 seconds if still on this page
    const fallbackTimer = setTimeout(() => {
      setShowFallback(true);
      setIsRetrying(false);
    }, 4000);
    
    return () => clearTimeout(fallbackTimer);
  }, []);

  const handleRetry = () => {
    setShowFallback(false);
    initPKCEFlow();
  };

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center space-y-4">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="outline" onClick={handleRetry}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center space-y-4">
        <Loader2 className="w-10 h-10 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-sm text-muted-foreground">Redirecting to Microsoft login...</p>
        
        {/* Fallback button after timeout */}
        {showFallback && !isRetrying && (
          <div className="pt-4 space-y-3">
            <div className="p-3 bg-muted rounded-lg max-w-xs mx-auto">
              <p className="text-xs text-muted-foreground">
                If you're still here, click below to continue.
              </p>
            </div>
            <Button 
              variant="default" 
              size="lg"
              onClick={handleRetry}
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
              </svg>
              Continue to Microsoft
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
